/* Setup — draft model. The account-level settings are one draft, autosaved as a
   diff against what was last saved (only the changed columns are sent). The
   per-list overrides are a second draft, saved row by row. */

import type { Account, FolderAiSetup, WorkWindow } from '../../../../api/client';
import { resolveAccountBaseRegime } from '../../aiRegimeOptions';
import { interruptValuesFromRow, type FolderInterruptFieldKey } from '../../folderInterruptOverrides';

export type Scope = 'all_folders' | 'specific_folders';
export type HotMode = 'horny' | 'moderate' | 'shy' | 'custom';
export type SchedMode = 'always_on' | 'scheduled';
export const HOT_MODES: HotMode[] = ['horny', 'moderate', 'shy', 'custom'];

/** Lists that hold system chats — removing one from the ignore lists asks first. */
export const PROTECTED_LISTS = ['Notifications', 'ignored_spam'];

export interface Step { min: number; max: number }

export interface Draft {
  scope: Scope;
  folders: string[];
  ignore: string[];
  regime: string;
  chatter: boolean;
  hot: HotMode;
  hotPrompt: string;
  fu: boolean;
  fuN: number;
  steps: Step[];
  autoDel: boolean;
  delMin: number;
  sched: SchedMode;
  tz: string;
  windows: WorkWindow[];
}

const isHot = (v: unknown): v is HotMode => HOT_MODES.includes(v as HotMode);
const toMin = (s: number) => Math.max(1, Math.round(s / 60));

export function draftOf(a: Account): Draft {
  return {
    scope: (a.ai_working_scope as Scope) || 'all_folders',
    folders: a.ai_selected_folders || [],
    ignore: a.lists_to_ignore || [],
    regime: resolveAccountBaseRegime(a),
    chatter: a.ai_call_chatter_on_completion ?? a.call_chatter_on_regime_completion ?? true,
    hot: isHot(a.ai_hot_transition_mode) ? a.ai_hot_transition_mode : 'moderate',
    hotPrompt: a.ai_hot_transition_custom_prompt ?? '',
    fu: a.allow_follow_up_messages ?? true,
    fuN: Math.min(3, Math.max(1, a.follow_up_messages_count ?? 1)),
    steps: [
      { min: toMin(a.follow_up_messages_time_min ?? 300), max: toMin(a.follow_up_messages_time_max ?? 600) },
      { min: toMin(a.follow_up_2_time_min ?? 600), max: toMin(a.follow_up_2_time_max ?? 1200) },
      { min: toMin(a.follow_up_3_time_min ?? 1200), max: toMin(a.follow_up_3_time_max ?? 1800) },
    ],
    autoDel: a.auto_delete_follow_ups ?? false,
    delMin: toMin(a.follow_up_delete_after_seconds ?? 3600),
    sched: (a.schedule_mode as SchedMode) || 'always_on',
    tz: a.schedule_timezone || 'Europe/Kyiv',
    windows: a.work_schedule || [],
  };
}

/** The account columns a draft maps to. Windows are only sent while Scheduled,
 *  so switching to Always on keeps them for switching back. */
export function payloadOf(d: Draft): Record<string, unknown> {
  const st = (i: number) => d.steps[i] ?? { min: 1, max: 1 };
  const [s1, s2, s3] = [st(0), st(1), st(2)];
  const out: Record<string, unknown> = {
    ai_working_scope: d.scope,
    ai_selected_folders: d.scope === 'specific_folders' ? d.folders : [],
    ai_base_regime: d.regime,
    default_automation_regime: d.regime,
    // Sent as chosen for every regime: the brain ignores it where it doesn't apply, and
    // keeping it means a trip through another regime can't silently switch hand-off off.
    ai_call_chatter_on_completion: d.chatter,
    ai_hot_transition_mode: d.hot,
    ai_hot_transition_custom_prompt: d.hot === 'custom' ? d.hotPrompt.trim() || null : null,
    lists_to_ignore: d.ignore,
    allow_follow_up_messages: d.fu,
    follow_up_messages_count: d.fuN,
    follow_up_messages_time_min: s1.min * 60,
    follow_up_messages_time_max: Math.max(s1.min, s1.max) * 60,
    follow_up_2_time_min: s2.min * 60,
    follow_up_2_time_max: Math.max(s2.min, s2.max) * 60,
    follow_up_3_time_min: s3.min * 60,
    follow_up_3_time_max: Math.max(s3.min, s3.max) * 60,
    auto_delete_follow_ups: d.autoDel,
    follow_up_delete_after_seconds: d.delMin * 60,
    schedule_mode: d.sched,
    schedule_timezone: d.tz,
  };
  if (d.sched === 'scheduled') out.work_schedule = d.windows;
  return out;
}

/* ── List overrides ─────────────────────────────────────────────── */
export type ReqMode = 'handoff' | 'automate' | 'decline';

export interface Override {
  id: string;
  list: string;
  regime: string;
  chatter: boolean;
  hot: HotMode;
  prompt: string;
  ints: Record<FolderInterruptFieldKey, boolean>;
  content: 'handoff' | 'automate' | null;
  custom: ReqMode | null;
  call: ReqMode | null;
  ppv: boolean | null;
}

export function overrideOf(s: FolderAiSetup): Override {
  return {
    id: 'srv-' + s.list_uuid,
    list: s.list_uuid,
    regime: s.base_regime || 'talk_and_tease',
    chatter: !!s.call_chatter_on_completion,
    hot: isHot(s.hot_transition_mode) ? s.hot_transition_mode : 'moderate',
    prompt: s.hot_transition_custom_prompt || '',
    ints: interruptValuesFromRow(s),
    content: s.content_automate_mode ?? null,
    custom: s.custom_request_mode ?? null,
    call: s.call_request_mode ?? null,
    ppv: typeof s.ppv_offer_active === 'boolean' ? s.ppv_offer_active : null,
  };
}

export function overridePayload(o: Override) {
  return {
    list_uuid: o.list,
    base_regime: o.regime,
    call_chatter_on_completion: o.chatter,
    hot_transition_mode: o.hot,
    hot_transition_custom_prompt: o.hot === 'custom' ? o.prompt.trim() || null : null,
    ...o.ints,
    content_automate_mode: o.content,
    custom_request_mode: o.custom,
    call_request_mode: o.call,
    ppv_offer_active: o.ppv,
  };
}

/* ── Work schedule ──────────────────────────────────────────────── */
/* The server stores one window per day ({day, start, end, …overrides}). The
   editor shows windows with day toggles instead: windows that differ only by
   day are one editor window. Times are minutes from local midnight; end 1440 is
   end of day and end <= start runs past midnight. */

export interface EdWindow extends Omit<WorkWindow, 'day'> { key: string; days: boolean[] }

let seq = 0;
const nextKey = () => 'w' + ++seq;

export function groupWindows(ws: WorkWindow[]): EdWindow[] {
  const out: EdWindow[] = [];
  const byKey = new Map<string, EdWindow>();
  ws.forEach((w) => {
    if (typeof w.day !== 'number' || w.day < 0 || w.day > 6) return;
    const { day, ...rest } = w;
    const k = JSON.stringify(rest);
    let g = byKey.get(k);
    if (!g) {
      g = { ...rest, key: nextKey(), days: [false, false, false, false, false, false, false] };
      byKey.set(k, g);
      out.push(g);
    }
    g.days[day] = true;
  });
  return out;
}

export function flattenWindows(gs: EdWindow[]): WorkWindow[] {
  return gs.flatMap((g) => {
    const { days, ...rest } = g;
    delete (rest as Partial<EdWindow>).key;
    return days.flatMap((on, day) => (on ? [{ day, ...rest }] : []));
  });
}

export const newWindow = (): EdWindow => ({ key: nextKey(), days: [true, true, true, true, true, true, true], start: 9 * 60, end: 17 * 60, regime: null });

/** Settings the window changes while it's active (regime not counted). */
export function windowChanges(w: Omit<WorkWindow, 'day'>): number {
  return (Array.isArray(w.ignore_lists) ? 1 : 0) + (w.interrupts ? Object.keys(w.interrupts).length : 0)
    + (w.ppv_active == null ? 0 : 1) + (w.content_mode ? 1 : 0) + (w.custom_mode ? 1 : 0) + (w.call_mode ? 1 : 0);
}

const pad = (n: number) => (n < 10 ? '0' + n : String(n));
export const hhmm = (m: number) => (m >= 1440 ? '00:00' : `${pad(Math.floor(m / 60))}:${pad(m % 60)}`);
export function parseHhmm(s: string): number | null {
  const m = /^(\d{1,2}):(\d{2})/.exec(s);
  if (!m) return null;
  const v = Number(m[1]) * 60 + Number(m[2]);
  return v >= 0 && v < 1440 ? v : null;
}

/** Hours the windows cover per week. */
export function weeklyHours(ws: WorkWindow[]): number {
  return ws.reduce((t, w) => t + ((w.end > w.start ? w.end - w.start : 1440 - w.start + w.end) / 60), 0);
}

/** Per-day coloured segments [from%, width%, changesRegime] for the week bars. */
export function weekSegments(ws: WorkWindow[]): [number, number, boolean][][] {
  const days: [number, number, boolean][][] = [[], [], [], [], [], [], []];
  const push = (d: number, a: number, b: number, r: boolean) => { if (b > a) days[d]?.push([(a / 1440) * 100, ((b - a) / 1440) * 100, r]); };
  ws.forEach((w) => {
    if (w.day < 0 || w.day > 6) return;
    const r = !!w.regime;
    if (w.end > w.start) push(w.day, w.start, Math.min(w.end, 1440), r);
    else { push(w.day, w.start, 1440, r); push((w.day + 1) % 7, 0, w.end, r); }
  });
  return days;
}
